import React, { useState, useEffect, useCallback } from 'react';
import { UserCog, Plus, Edit2, Trash2, X, Eye, EyeOff } from 'lucide-react';
import {
  fetchSuperUsers,
  fetchRoles,
  createSuperUser,
  updateSuperUser,
  deleteSuperUser,
  SuperUserData,
  RoleData,
} from '../services/api.ts';
import { useAuth } from '../context/AuthContext.tsx';
import './SuperUsers.css';

interface FormData {
  username: string;
  email: string;
  phone: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  password: string;
  role_id: number | '';
  enable: boolean;
}

const emptyForm: FormData = {
  username: '',
  email: '',
  phone: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  password: '',
  role_id: '',
  enable: true,
};

const SuperUsers: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [superusers, setSuperusers] = useState<SuperUserData[]>([]);
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<SuperUserData | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([fetchSuperUsers(), fetchRoles()]);
      setSuperusers(usersData);
      setRoles(rolesData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load super users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (su: SuperUserData) => {
    setEditingUser(su);
    setFormData({
      username: su.username,
      email: su.email,
      phone: su.phone || '',
      first_name: su.first_name || '',
      middle_name: su.middle_name || '',
      last_name: su.last_name || '',
      password: '',
      role_id: su.role_id || '',
      enable: su.enable,
    });
    setShowPassword(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(emptyForm);
  };

  const handleChange = (field: keyof FormData, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.username.trim() || !formData.email.trim() || !formData.role_id) return;

    setSaving(true);
    try {
      if (editingUser) {
        const updateData: Record<string, any> = {
          username: formData.username.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined,
          first_name: formData.first_name.trim() || undefined,
          middle_name: formData.middle_name.trim() || undefined,
          last_name: formData.last_name.trim() || undefined,
          enable: formData.enable,
          role_id: Number(formData.role_id),
        };
        if (formData.password.trim()) {
          updateData.password = formData.password.trim();
        }
        await updateSuperUser(editingUser.id, updateData);
      } else {
        if (!formData.password.trim()) {
          setError('Password is required for new users');
          setSaving(false);
          return;
        }
        await createSuperUser({
          username: formData.username.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined,
          first_name: formData.first_name.trim() || undefined,
          middle_name: formData.middle_name.trim() || undefined,
          last_name: formData.last_name.trim() || undefined,
          password: formData.password.trim(),
          role_id: Number(formData.role_id),
        });
      }
      closeModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSuperUser(id);
      setDeleteConfirm(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete user');
      setDeleteConfirm(null);
    }
  };

  const getRoleName = (roleId: number | null) => {
    if (!roleId) return 'No Role';
    const role = roles.find((r) => r.id === roleId);
    return role ? role.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="superusers-page">
        <div className="superusers-loading">
          <div className="spinner-large" />
          <p>Loading super users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="superusers-page">
      <div className="superusers-header">
        <div className="superusers-header-left">
          <UserCog size={28} />
          <div>
            <h1>Super Users</h1>
            <p>Manage admin accounts and role assignments</p>
          </div>
        </div>
        <button className="su-btn-primary" onClick={openCreateModal}>
          <Plus size={18} />
          Add Super User
        </button>
      </div>

      {error && (
        <div className="su-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="su-table-wrapper">
        <table className="su-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {superusers.map((su) => (
              <tr key={su.id}>
                <td>
                  <div className="su-user-cell">
                    <div className="su-avatar">{su.username.charAt(0).toUpperCase()}</div>
                    <div>
                      <div className="su-user-name">{su.username}</div>
                      {su.first_name && (
                        <div className="su-user-fullname">
                          {[su.first_name, su.middle_name, su.last_name].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{su.email}</td>
                <td>
                  <span className="su-role-badge">{su.role_name || getRoleName(su.role_id)}</span>
                </td>
                <td>
                  <span className={`su-status ${su.enable ? 'active' : 'disabled'}`}>
                    {su.enable ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td>
                  <div className="su-actions">
                    <button className="su-action-btn edit" onClick={() => openEditModal(su)} title="Edit">
                      <Edit2 size={16} />
                    </button>
                    {String(su.id) !== String(currentUser?.id) && (
                      <button
                        className="su-action-btn delete"
                        onClick={() => setDeleteConfirm(su.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  {deleteConfirm === su.id && (
                    <div className="su-delete-popup">
                      <p>Delete <strong>{su.username}</strong>?</p>
                      <div className="su-delete-actions">
                        <button className="su-btn-danger" onClick={() => handleDelete(su.id)}>Delete</button>
                        <button className="su-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {superusers.length === 0 && (
              <tr>
                <td colSpan={5} className="su-empty">No super users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="su-modal-overlay" onClick={closeModal}>
          <div className="su-modal" onClick={(e) => e.stopPropagation()}>
            <div className="su-modal-header">
              <h2>{editingUser ? 'Edit Super User' : 'Add Super User'}</h2>
              <button className="su-modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="su-modal-body">
              <div className="su-form-row">
                <div className="su-form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleChange('username', e.target.value)}
                    placeholder="Username"
                  />
                </div>
                <div className="su-form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Email"
                  />
                </div>
              </div>

              <div className="su-form-row">
                <div className="su-form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="First Name"
                  />
                </div>
                <div className="su-form-group">
                  <label>Middle Name</label>
                  <input
                    type="text"
                    value={formData.middle_name}
                    onChange={(e) => handleChange('middle_name', e.target.value)}
                    placeholder="Middle Name"
                  />
                </div>
                <div className="su-form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Last Name"
                  />
                </div>
              </div>

              <div className="su-form-row">
                <div className="su-form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
                <div className="su-form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role_id}
                    onChange={(e) => handleChange('role_id', e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>{role.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="su-form-group">
                <label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                <div className="su-password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                  />
                  <button type="button" className="su-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {editingUser && (
                <div className="su-form-group">
                  <label className="su-toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.enable}
                      onChange={(e) => handleChange('enable', e.target.checked)}
                    />
                    <span>Account Enabled</span>
                  </label>
                </div>
              )}
            </div>

            <div className="su-modal-footer">
              <button className="su-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="su-btn-primary"
                onClick={handleSave}
                disabled={saving || !formData.username.trim() || !formData.email.trim() || !formData.role_id}
              >
                {saving ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperUsers;
